import { api } from "./api";

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

/** iPhone/iPad only allow push for apps added to the Home Screen (iOS 16.4+). */
export const needsInstallForPush = () =>
  /iP(hone|ad|od)/.test(navigator.userAgent) &&
  !window.matchMedia("(display-mode: standalone)").matches;

const toKey = (b64url: string) => {
  const pad = "=".repeat((4 - (b64url.length % 4)) % 4);
  const raw = atob((b64url + pad).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
};

async function registration() {
  return (await navigator.serviceWorker.getRegistration()) ?? (await navigator.serviceWorker.ready);
}

export async function currentSubscription(): Promise<PushSubscription | null> {
  if (!pushSupported()) return null;
  try {
    return await (await registration()).pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Ask permission, subscribe this device and tell the server. Throws a readable Error on failure. */
export async function enablePush(): Promise<void> {
  if (!pushSupported()) throw new Error("This browser does not support background notifications.");
  if (needsInstallForPush())
    throw new Error(
      "On iPhone, add the app to the Home Screen first (Share → Add to Home Screen).",
    );
  const perm = await Notification.requestPermission();
  if (perm !== "granted")
    throw new Error("Notifications are blocked for this site in the browser settings.");
  const { publicKey } = await api<{ publicKey: string }>("/push/key");
  const reg = await registration();
  const sub =
    (await reg.pushManager.getSubscription()) ??
    (await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: toKey(publicKey),
    }));
  await api("/push/subscribe", { method: "POST", body: { endpoint: sub.endpoint } });
}

export async function disablePush(): Promise<void> {
  const sub = await currentSubscription();
  if (sub) {
    await api("/push/unsubscribe", { method: "POST", body: { endpoint: sub.endpoint } }).catch(
      () => undefined,
    );
    await sub.unsubscribe().catch(() => undefined);
  }
}
